/* eslint-disable indent */
import './styles.scss';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { useForm, useFormState } from 'react-hook-form';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { yupResolver } from '@hookform/resolvers/yup';
import PropTypes from 'prop-types';
import {
  adjustmentCommentsSchema,
  adjustmentRejectSchema,
} from 'features/SubmitProjectProposal/validation';
import {
  downloadScientificProfileFile,
  getAllOrganizations,
  getAllProjectRoles,
  getGeneralInfoById,
} from 'apis/projectProposals.api';
import { toast } from 'layout';
import { ADJUSTMENT_STATUSES, ADJUSTMENT_TYPES, PROJECT_ROLES, TOAST } from 'constant';
import { getByEmail as getContactByEmail } from 'apis/contacts.api';

import {
  getAcadamicLevelShortHand,
  mapError,
  modifyBindingProjectProposalGeneralData,
  toNonAccentVietnamese,
} from 'utils/func';
import {
  HeaderInfo,
  PreResearchersTable,
} from 'features/SubmitProjectProposal/components/EditHumanResource';
import Loading from 'components/Loading';
// import ErrorDialog from 'components/ErrorDialog';
import { useGetPersonnelAdjustmentById } from 'utils/hooks/personnelAdjustments';
import AdjustmentComment from 'features/SubmitProjectProposal/components/AdjustmentComment';
import {
  useCreateAdjustmentComment,
  useGetAllAdjustmentComments,
} from 'utils/hooks/adjustmentComments';
import RejectModal from 'features/SubmitProjectProposal/components/RejectModal';
import { reviewPersonnelAdjustment } from 'apis/personnelAdjustment.api';
import ResearchersTable from './ResearchersTable';
import DownloadPersonnelAdjustmentFile from './DownloadPersonnelAdjustmentFile';

function EditHumanResource({ id, isSumUp }) {
  const { personnelAdjustmentId, projectProposalId } = useParams();
  const [searchParams] = useSearchParams();
  const projectProposalIdUsed = projectProposalId || searchParams.get('projectProposalId');
  const { t } = useTranslation();
  const navigate = useNavigate();
  //   const [isVisibleErrorDialog, setIsVisibleDialogError] = useState(false);
  const [isCommentVisible, setIsCommentVisible] = useState(false);
  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [approveModalVisible, setApproveModalVisible] = useState(false);
  const [schema, setSchema] = useState(adjustmentCommentsSchema);

  const personnelAdjustmentIdUsed = personnelAdjustmentId || id;

  const { data: generalInfoData, isLoading: isGeneralInfoLoading } = useQuery(
    ['projectProposalGeneralInfo', projectProposalIdUsed],
    () => getGeneralInfoById(projectProposalIdUsed)
  );

  const proposalGeneralInfo = useMemo(() => generalInfoData?.data, [generalInfoData]);

  const { data: personnelAdjustment, refetch } = useGetPersonnelAdjustmentById(
    personnelAdjustmentIdUsed,
    {}
  );

  const isRejectedOrApproved = useMemo(
    () => personnelAdjustment?.status === ADJUSTMENT_STATUSES.SUBMITTED ?? null,
    [personnelAdjustment]
  );

  const { data: adjustmentComments, refetch: refetchAdjustmentComments } =
    useGetAllAdjustmentComments(
      {
        adjustmentName: ADJUSTMENT_TYPES.PERSONNEL_ADJUSTMENT,
        adjustmentId: personnelAdjustmentIdUsed,
      },
      {
        enabled: !!personnelAdjustmentIdUsed,
      }
    );

  const { mutateAsync } = useCreateAdjustmentComment();
  const { mutateAsync: mutateAsyncReview } = useMutation(reviewPersonnelAdjustment);

  const { mutate: mutateDownloadScientificProfileFile, isLoading: isDowloadProfileLoading } =
    useMutation(downloadScientificProfileFile);

  const { control, handleSubmit, reset, watch, setValue } = useForm({
    resolver: yupResolver(schema),
    mode: 'onChange',
  });
  const { errors, isDirty, dirtyFields } = useFormState({
    control,
  });

  const { mutate: bindContact } = useMutation(getContactByEmail);

  const { data: organizationsData, isLoading: isOrganizationLoading } = useQuery(
    ['projectProposalOrganizations'],
    getAllOrganizations,
    { staleTime: 1000 * 60 * 5 }
  );
  const { data: projectRolesData } = useQuery(['projectProposalProjectRoles'], getAllProjectRoles, {
    staleTime: 1000 * 60 * 5,
  });
  const organizations = useMemo(
    () =>
      organizationsData?.data.map((e) => ({
        ...e,
        name: t('lang') === 'vi' ? e?.name : e?.englishName,
      })) ?? [],
    [organizationsData, t]
  );

  const owner = useMemo(
    () =>
      proposalGeneralInfo?.projectProposalContacts?.find(
        (item) => item?.projectRole?.code === PROJECT_ROLES.OWNER
      ) ?? {},
    [proposalGeneralInfo]
  );

  const projectRoles = useMemo(() => projectRolesData?.data ?? [], [projectRolesData]);

  const handleBindingIntoContactFieldClick = (email, update, index, value) => {
    bindContact(
      { email },
      {
        onSuccess: (res) => {
          if (res?.data) {
            update(index, {
              ...value,
              email: res.data.email,
              contact: res.data,
              fullname: res?.data?.fullname,
              gender: res?.data?.scientificProfile?.gender ?? '',
              academicDegree: res?.data?.scientificProfile?.academicDegree ?? '',
              academicRank: res?.data?.scientificProfile?.academicRank ?? '',
              researchLines: res?.data?.scientificProfile?.researchLines ?? [],
              organizationId: res?.data?.organizationId,
              specificOrganizationName: res?.data?.specificOrganizationName,
            });
            toast(TOAST.SUCCESS, t('projectProposal.getDataSuccessMessage'));
          }
        },
        onError: () => {
          toast(TOAST.ERROR, t('projectProposal.getDataErrorMessage'));
        },
      }
    );
  };

  const handleDownloadScientificProfileFile = (
    contactId,
    contactName = '',
    academicRank = '',
    academicDegree = ''
  ) => {
    if (contactId && contactName) {
      mutateDownloadScientificProfileFile(contactId, {
        onSuccess: async (res) => {
          if (res?.data) {
            const file = new Blob([res.data]);

            const link = document.createElement('a');
            link.href = URL.createObjectURL(file);
            link.download = `${getAcadamicLevelShortHand(
              academicRank,
              academicDegree,
              t,
              true
            )}${toNonAccentVietnamese(contactName)}.pdf`;

            link.click();
          }
        },
        onError: () => {
          toast(
            TOAST.ERROR,
            t('projectProposal.general.researchHumanResources.downloadScientificProfileErrorTitle')
          );
        },
      });
    }
  };

  useEffect(() => {
    const data = personnelAdjustment;
    if (data && Object.keys(data).length > 0) {
      let oldData = data;
      oldData = {
        ...data?.oldMembers?.find((item) => item?.projectRole?.code === PROJECT_ROLES.OWNER),
        projectProposalContacts: data?.oldMembers,
      };

      let newData = data;
      newData = {
        ...data?.newMembers?.find((item) => item?.projectRole?.code === PROJECT_ROLES.OWNER),
        projectProposalContacts: data?.newMembers,
      };
      const preBindingGeneralData = modifyBindingProjectProposalGeneralData(oldData);
      const bindingGeneralData = modifyBindingProjectProposalGeneralData(newData);
      // preGeneralData
      setValue('preGeneral', preBindingGeneralData);
      setValue('preGeneral.recommendedExperts', preBindingGeneralData.recommendedExperts);
      setValue('preGeneral.researchExpense', preBindingGeneralData.researchExpense);
      setValue('preGeneral.owner.researchLines', preBindingGeneralData?.owner?.researchLines ?? []);
      setValue('preGeneral.partners', preBindingGeneralData.partners);
      setValue('preGeneral.scientificSecretaries', preBindingGeneralData.scientificSecretaries);
      setValue('preGeneral.mainParticipants', preBindingGeneralData.mainParticipants);
      setValue('preGeneral.participants', preBindingGeneralData.participants);
      setValue('preGeneral.technicians', preBindingGeneralData.technicians);
      setValue('preGeneral.domesticExperts', preBindingGeneralData.domesticExperts);
      setValue('preGeneral.internationalExperts', preBindingGeneralData.internationalExperts);

      // generalData
      setValue('general', bindingGeneralData);
      setValue('general.recommendedExperts', bindingGeneralData.recommendedExperts);
      setValue('general.researchExpense', bindingGeneralData.researchExpense);
      setValue('general.owner.researchLines', bindingGeneralData?.owner?.researchLines ?? []);
      setValue('general.owners', bindingGeneralData.owners);
      setValue('general.partners', bindingGeneralData.partners);
      setValue('general.scientificSecretaries', bindingGeneralData.scientificSecretaries);
      setValue('general.mainParticipants', bindingGeneralData.mainParticipants);
      setValue('general.participants', bindingGeneralData.participants);
      setValue('general.technicians', bindingGeneralData.technicians);
      setValue('general.domesticExperts', bindingGeneralData.domesticExperts);
      setValue('general.internationalExperts', bindingGeneralData.internationalExperts);
    }
  }, [personnelAdjustment]);

  const onSubmitComment = async (data) => {
    const dataSender = {
      ...data,
      adjustmentName: ADJUSTMENT_TYPES.PERSONNEL_ADJUSTMENT,
      adjustmentId: personnelAdjustmentIdUsed,
    };
    try {
      const res = await mutateAsync(dataSender);
      if (res?.status.toString().startsWith('2')) {
        toast(TOAST.SUCCESS, t('projectAdjustment.commentSuccess'));
        refetchAdjustmentComments();
        reset();
      }
    } catch (error) {
      toast(TOAST.ERROR, mapError(t, error));
    }
  };

  const onSubmit = async (data) => {
    const dataSender = {};
    if (rejectModalVisible) {
      dataSender.status = ADJUSTMENT_STATUSES.REJECTED;
      dataSender.rejectionReason = data?.rejectionReason;
    } else if (approveModalVisible) {
      dataSender.status = ADJUSTMENT_STATUSES.APPROVED;
    }
    try {
      const res = await mutateAsyncReview({ id: personnelAdjustmentIdUsed, body: dataSender });
      if (res?.status.toString().startsWith('2')) {
        toast(
          TOAST.SUCCESS,
          t(`projectAdjustment.${rejectModalVisible ? 'rejectModal' : 'approveModal'}.success`)
        );
        refetch();
        setSchema(adjustmentCommentsSchema);
        setRejectModalVisible(false);
        setApproveModalVisible(false);
      }
    } catch (error) {
      toast(TOAST.ERROR, mapError(t, error));
    }
  };

  const footerConfirmSubmit = () => (
    <div>
      <Button
        label={t('formLayout.action.cancel')}
        className="w-7rem"
        severity="danger"
        onClick={() => setApproveModalVisible(false)}
        autoFocus
      />
      <Button
        label={t('formLayout.action.ok')}
        className="w-7rem"
        severity="info"
        type="button"
        onClick={onSubmit}
      />
    </div>
  );

  const onCloseRejectModal = () => {
    reset({}, { keepValues: true });
    setSchema(adjustmentCommentsSchema);
    setRejectModalVisible(false);
  };
  return (
    <>
      {isOrganizationLoading && isDowloadProfileLoading && isGeneralInfoLoading && <Loading />}
      {/* {isErrorRes && (
        <ErrorDialog
          title={t('editHumanResource.action.edit')}
          isError={isErrorRes}
          error={errorRes}
          visible={isVisibleErrorDialog}
          setVisible={setIsVisibleDialogError}
        />
      )} */}
      <DownloadPersonnelAdjustmentFile id={personnelAdjustmentIdUsed} />
      <div className="p-5 edit-human-resource-form">
        <HeaderInfo />
        <h5>{t('editHumanResource.proposal')}</h5>

        <div className="flex mb-1">
          <div className="mx-2 font-semibold">
            - {t('projectProposal.general.projectNameAndType.vietnameseName')}:
          </div>
          <div className="flex-1">{proposalGeneralInfo?.vietnameseName}</div>
        </div>
        <div className="flex mb-1">
          <div className="mx-2 font-semibold">
            - {t('projectProposal.general.projectNameAndType.englishName')}:{' '}
          </div>
          <div className="flex-1">{proposalGeneralInfo?.englishName}</div>
        </div>

        <div className="flex my-1 align-items-center">
          <div className="mx-2 font-semibold"> - {t('financialAdjustment.owner')} </div>
          <p className="flex-1">
            {getAcadamicLevelShortHand(owner?.academicRank, owner?.academicDegree, t, true)}
            <span>{owner?.fullname}</span>
          </p>
        </div>

        <h5>{t('editHumanResource.preHumanResource')}</h5>
        <PreResearchersTable
          organizations={organizations}
          projectRoles={projectRoles}
          watch={watch}
          control={control}
          errors={errors}
          handleDownloadScientificProfileFile={handleDownloadScientificProfileFile}
        />

        <h5>{t('editHumanResource.postHumanResource')}</h5>
        <ResearchersTable
          organizations={organizations}
          projectRoles={projectRoles}
          watch={watch}
          control={control}
          errors={errors}
          onBindingIntoContactFieldClick={handleBindingIntoContactFieldClick}
          handleDownloadScientificProfileFile={handleDownloadScientificProfileFile}
          disabled
        />

        <AdjustmentComment
          visible={isCommentVisible}
          onClose={() => setIsCommentVisible(false)}
          control={control}
          errors={errors}
          handleSubmit={handleSubmit}
          onSubmit={onSubmitComment}
          data={adjustmentComments}
          status={personnelAdjustment?.status}
          disabled={!(isDirty && Object.keys(dirtyFields).length) || rejectModalVisible}
        />

        <div
          className={`flex justify-content-end gap-4 ${
            !isSumUp || isRejectedOrApproved ? 'mt-6' : ''
          }`}
        >
          {!isSumUp && (
            <Button
              className="w-10rem"
              style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
              label={t('extendImplementationTime.comeBack')}
              severity="danger"
              type="button"
              onClick={() => {
                navigate(-1);
              }}
            />
          )}
          {isRejectedOrApproved && (
            <>
              <Button
                className="w-10rem"
                style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                label={t('projectAdjustment.reject')}
                severity="warning"
                type="button"
                onClick={() => {
                  setSchema(adjustmentRejectSchema);
                  setRejectModalVisible(true);
                }}
                disabled={!isRejectedOrApproved}
              />
              <Button
                className="w-16rem"
                style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                label={t('projectAdjustment.approve')}
                severity="success"
                type="button"
                onClick={() => setApproveModalVisible(true)}
                disabled={!isRejectedOrApproved}
              />
            </>
          )}
        </div>
      </div>

      <RejectModal
        visible={rejectModalVisible}
        onClose={onCloseRejectModal}
        control={control}
        errors={errors}
        handleSubmit={handleSubmit}
        onSubmit={onSubmit}
        isDeadline={false}
      />

      <Dialog
        visible={approveModalVisible}
        header={<p className="text-blue-500">{t('editHumanResource.dialogConfirm.title')}</p>}
        position="center"
        style={{ width: '410px' }}
        onHide={() => setApproveModalVisible(false)}
        footer={() => footerConfirmSubmit()}
      >
        <div className="text-lg py-3">{t('editHumanResource.dialogConfirm.content')}</div>
        <span className="text-md py-3 ">{t('editHumanResource.dialogConfirm.attention')}</span>
        <span className="text-md py-3 font-bold">
          {t('editHumanResource.dialogConfirm.warning')}
        </span>
      </Dialog>
    </>
  );
}

EditHumanResource.propTypes = {
  id: PropTypes.string,
  isSumUp: PropTypes.bool,
};

EditHumanResource.defaultProps = {
  id: null,
  isSumUp: false,
};

export default EditHumanResource;
